"use client";
import React, { useState, useMemo, useCallback } from "react";
import {
  Search,
  Filter,
  Plus,
  Edit3,
  Trash2,
  Grid3X3,
  ChevronDown,
  Layers,
  Table,
  CheckCircle,
} from "lucide-react";
import CategoryModal from "./CategoryModal";
import { useCategories } from "../components/context/CategoryContext";

const CategoryPage = () => {
  const {
    categories = [],
    loading,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleStatus,
  } = useCategories();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [ActiveDropdown, setActiveDropdown] = useState(null);

  /* ── Toast State ── */
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

  const showToast = useCallback((message = "Status updated successfully!", type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  }, []);

  const statusOptions = [
    { value: "all", label: "All Categories", count: categories.length },
    { value: "Active", label: "Active", count: categories.filter((c) => c.status === "Active").length },
    { value: "Inactive", label: "Inactive", count: categories.filter((c) => c.status === "Inactive").length },
  ];

  const sortOptions = [
    { value: "name", label: "Name A-Z" },
    { value: "name-desc", label: "Name Z-A" },
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
  ];

  const filteredAndSortedCategories = useMemo(() => {
    let filtered = categories.filter((category) => {
      const matchesSearch =
        searchTerm === "" ||
        category.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === "all" || category.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    const parseDate = (value) => {
      if (!value) return 0;
      const normalized = value.replace(" ", "T");
      const time = new Date(normalized).getTime();
      return isNaN(time) ? 0 : time;
    };

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name": return (a.name || "").localeCompare(b.name || "");
        case "name-desc": return (b.name || "").localeCompare(a.name || "");
        case "newest": return parseDate(b.create_audit_time) - parseDate(a.create_audit_time);
        case "oldest": return parseDate(a.create_audit_time) - parseDate(b.create_audit_time);
        default: return 0;
      }
    });

    return filtered;
  }, [categories, searchTerm, statusFilter, sortBy]);

  const handleSelectAll = () => {
    if (selectedCategories.length === filteredAndSortedCategories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(filteredAndSortedCategories.map((c) => c.id));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) {
      setActiveDropdown(null);
      return;
    }
    try {
      await deleteCategory(categoryId);
      setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setActiveDropdown(null);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowAddModal(true);
    setActiveDropdown(null);
  };

  /* ── Toggle Status with Toast ── */
  const handleToggleStatus = async (categoryId, currentStatus) => {
    try {
      await toggleStatus(categoryId, currentStatus);
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
      showToast(`Status updated to ${newStatus} successfully!`);
    } catch (err) {
      console.error("Toggle status failed:", err);
      showToast("Failed to update status.", "error");
    } finally {
      setActiveDropdown(null);
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryData);
      } else {
        await addCategory(categoryData);
      }
    } catch (err) {
      console.error("Save category failed:", err);
      return;
    } finally {
      setEditingCategory(null);
      setShowAddModal(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowAddModal(true);
  };

  /* ── Toast Component ── */
  const Toast = () => (
    <div
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 9999,
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        opacity: toast.visible ? 1 : 0,
        transform: toast.visible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.97)",
        pointerEvents: toast.visible ? "auto" : "none",
      }}
    >
      <div
        className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === "error"
            ? "bg-red-600 text-white"
            : "bg-teal-600 text-white"
        }`}
      >
        {toast.type === "error" ? (
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
        ) : (
          <CheckCircle className="w-5 h-5 shrink-0" />
        )}
        {toast.message}
      </div>
    </div>
  );

  /* ── Reusable Toggle Switch ── */
  const ToggleSwitch = ({ categoryId, status }) => (
    <label
      className="relative inline-flex items-center cursor-pointer"
      title={status === "Active" ? "Set Inactive" : "Set Active"}
    >
      <input
        type="checkbox"
        checked={status === "Active"}
        onChange={() => handleToggleStatus(categoryId, status)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors duration-300 shadow-sm"></div>
      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 peer-checked:translate-x-5"></div>
    </label>
  );

  /* ── Grid Card ── */
  const CategoryCard = ({ category }) => (
    <div className="bg-white rounded-xl border border-orange-200 overflow-hidden hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300 transform hover:-translate-y-1 group">
      <div className="relative w-full h-40 sm:h-48">
        <img
          src={category.image}
          alt={category.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 right-3">
          <ToggleSwitch categoryId={category.id} status={category.status} />
        </div>
      </div>
      <div className="p-3 sm:p-4 flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-semibold text-teal-800 group-hover:text-teal-900 truncate">
            {category.name}
          </h3>
          <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">
            {category.description}
          </p>
        </div>
        <div className="flex flex-row gap-1.5 sm:gap-2 shrink-0 mt-10 sm:mt-12">
          <button
            onClick={() => handleEditCategory(category)}
            className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors text-xs font-medium"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => handleDeleteCategory(category.id)}
            className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-xs font-medium"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">

        {/* ── Page Header ── */}
        <header className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-600 rounded-xl flex items-center justify-center shrink-0">
              <Grid3X3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-teal-800">Categories</h1>
              <p className="text-sm text-gray-600 mt-0.5">Manage your product categories</p>
            </div>
          </div>
          <button
            onClick={handleAddCategory}
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            Add Category
          </button>
        </header>

        {/* ── Search & Filters ── */}
        <section className="bg-white bg-opacity-60 rounded-xl border border-orange-200 p-4 sm:p-6 mb-6 sm:mb-8 hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
            <div className="relative w-full sm:w-1/2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 text-teal-600 hover:text-teal-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-lg px-3 py-2.5 border border-teal-200 hover:bg-teal-50 sm:border-none sm:hover:bg-transparent text-sm"
            >
              <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Filters</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({option.count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {selectedCategories.length > 0 && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Bulk Actions ({selectedCategories.length} selected)
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-xs sm:text-sm"
                      onClick={async () => {
                        if (!window.confirm("Delete all selected categories?")) return;
                        for (const id of selectedCategories) {
                          try { await deleteCategory(id); } catch (e) { console.error(e); }
                        }
                        setSelectedCategories([]);
                      }}
                    >
                      Delete Selected
                    </button>
                    <button
                      className="px-3 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors text-xs sm:text-sm"
                      onClick={async () => {
                        for (const id of selectedCategories) {
                          const cat = categories.find((c) => c.id === id);
                          if (!cat) continue;
                          try { await toggleStatus(id, cat.status); } catch (e) { console.error(e); }
                        }
                        showToast("Bulk status updated successfully!");
                      }}
                    >
                      Toggle Status
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {loading && (
          <div className="mb-4 text-sm text-gray-500 text-center py-2">Loading categories...</div>
        )}

        {/* ── Categories Section ── */}
        <section>
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <span className="text-xs sm:text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-teal-700">{filteredAndSortedCategories.length}</span>
              {" "}of{" "}
              <span className="font-semibold">{categories.length}</span>
              {" "}categories
            </span>
            <div className="flex items-center gap-1.5 sm:gap-2">
              {[
                { mode: "grid", Icon: Grid3X3 },
                { mode: "list", Icon: Layers },
                { mode: "table", Icon: Table },
              ].map(({ mode, Icon }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`p-1.5 sm:p-2 border rounded-lg transition-colors ${
                    viewMode === mode
                      ? "bg-teal-600 text-white border-teal-600"
                      : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200"
                  }`}
                >
                  <Icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                </button>
              ))}
            </div>
          </div>

          {filteredAndSortedCategories.length === 0 ? (
            <div className="text-center py-12 sm:py-16 px-4">
              <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <Grid3X3 className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4">No categories found</h2>
              <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filter criteria."
                  : "Create your first category to get started."}
              </p>
              <button
                onClick={handleAddCategory}
                className="bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
              >
                Add Category
              </button>
            </div>
          ) : (
            <>
              {viewMode === "grid" && (
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
               {filteredAndSortedCategories.map((category) => (
                    <CategoryCard key={category.id} category={category} />
                  ))}
                </div>
              )}

              {viewMode === "list" && (
                <div className="flex flex-col gap-2 sm:gap-3">
                  {filteredAndSortedCategories.map((category) => (
                    <div
                      key={category.id}
                      className={`bg-white border rounded-xl px-3 sm:px-5 py-3 sm:py-4 flex items-center gap-3 sm:gap-4 transition-all duration-200 ${
                        category.status === "Inactive"
                          ? "border-gray-200 opacity-60 grayscale"
                          : "border-gray-200 hover:shadow-md hover:border-teal-200"
                      }`}
                    >
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover border border-gray-100 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-gray-800 text-sm truncate">{category.name}</h3>
                        <p className="text-xs text-gray-500 truncate hidden xs:block">{category.description}</p>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                        <ToggleSwitch categoryId={category.id} status={category.status} />
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="p-1.5 sm:p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit3 size={14} className="sm:w-[15px] sm:h-[15px]" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} className="sm:w-[15px] sm:h-[15px]" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {viewMode === "table" && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[480px]">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-14 sm:w-16">Image</th>
                          <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Description</th>
                          <th className="px-3 sm:px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 sm:px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredAndSortedCategories.map((category) => (
                          <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-3 sm:px-4 py-3">
                              <img src={category.image} alt={category.name} className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg object-cover border border-gray-100" />
                            </td>
                            <td className="px-3 sm:px-4 py-3 font-medium text-gray-800 text-xs sm:text-sm">{category.name}</td>
                            <td className="px-3 sm:px-4 py-3 text-gray-500 text-xs sm:text-sm max-w-[180px] sm:max-w-xs truncate hidden md:table-cell">{category.description}</td>
                            <td className="px-3 sm:px-4 py-3 flex justify-end">
                              <ToggleSwitch categoryId={category.id} status={category.status} />
                            </td>
                            <td className="px-3 sm:px-4 py-3">
                              <div className="flex items-center justify-end gap-1 sm:gap-2">
                                <button onClick={() => handleEditCategory(category)} className="p-1.5 sm:p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Edit">
                                  <Edit3 size={14} className="sm:w-4 sm:h-4" />
                                </button>
                                <button onClick={() => handleDeleteCategory(category.id)} className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                                  <Trash2 size={14} className="sm:w-4 sm:h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* ── Category Modal ── */}
      <CategoryModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSave={handleSaveCategory}
      />

      {ActiveDropdown && (
        <div className="fixed inset-0 z-40" onClick={() => setActiveDropdown(null)} />
      )}

      {/* ── Toast Notification ── */}
      <Toast />
    </div>
  );
};

export default CategoryPage;